import { useEffect, useRef, useState } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import {
  type MatchWith,
  type MatcherDescriptor,
  type MatcherOperator,
} from "@north-beam/nb-common";
import { TertiaryButton } from "@shared/buttons";

import { OperatorSelector } from "./operator-selector";

export const MATCH_WITH_LABEL: Record<MatchWith, string> = {
  name: "Name",
  "utm.source": "utm_source",
  "utm.medium": "utm_medium",
  "utm.campaign": "utm_campaign",
  "utm.term": "utm_term",
  "utm.content": "utm_content",
  "custom-spend-sheet": "Spend Sheet/API Name",
  "custom-spend-api-platform": "Spend API Platform",
};

export const OPERATOR_LABEL: Record<MatcherOperator, string> = {
  "==": "Is",
  "!=": "Is Not",
  contains: "Contains",
  "!contains": "Does not contain",
  startsWith: "Starts with",
  endsWith: "Ends with",
  "~": "Matches Regex",
};

interface Props {
  matcher: MatcherDescriptor;
  onChange: (value: MatcherDescriptor) => void;
  onDelete: () => void;
}

export const MatcherRenderer = ({ matcher, onChange, onDelete }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState(matcher.value === "");
  const [isAboutToDelete, setIsAboutToDelete] = useState(false);
  const [matchWith, setMatchWith] = useState(matcher.matchWith);
  const [operator, setOperator] = useState(matcher.operator);
  const [value, setValue] = useState(matcher.value);

  useEffect(() => {
    setMatchWith(matcher.matchWith);
    setOperator(matcher.operator);
    setValue(matcher.value);
  }, [matcher]);

  const handleDone = () => {
    if (value === "") {
      onDelete();
      return;
    }

    setIsEditing(false);
    onChange({ ...matcher, matchWith, operator, value });
  };

  const handleMatcherBlurred = (updatedValue: string) => {
    setValue(updatedValue);
    onChange({ ...matcher, matchWith, operator, value: updatedValue });
  };

  const deleteButton = (
    <GenericTooltip
      className="ml-1"
      overlayClassName="pointer-events-none"
      noInfoCircle
      content="Delete criteria"
    >
      <TertiaryButton
        className={clsx(
          "w-6 h-6 p-0 ml-1 text-base flex items-center justify-center",
          "rounded bg-transparent hover:bg-white hover:text-mars group/close-button",
        )}
        onMouseEnter={() => setIsAboutToDelete(true)}
        onMouseLeave={() => setIsAboutToDelete(false)}
        onClick={() => onDelete()}
      >
        <i className="fa-regular fa-sharp fa-times group-hover/close-button:text-mars"></i>
      </TertiaryButton>
    </GenericTooltip>
  );

  return (
    <>
      {isEditing ? (
        <div className="flex items-stretch h-8 max-w-[280px] w-fit">
          <OperatorSelector
            className="mr-[-1px]"
            matchWith={matchWith}
            operator={operator}
            onChangeMatchWith={setMatchWith}
            onChangeOperator={setOperator}
          />
          <div
            className={clsx(
              "flex items-center border border-cosmic-1 hover:border-cosmic",
              "rounded-r text-sm",
            )}
          >
            <input
              ref={inputRef}
              className="px-2 w-full min-w-[48px]"
              type="text"
              value={value}
              autoFocus
              onChange={(e) => setValue(e.target.value)}
              onBlur={(e) => {
                handleMatcherBlurred(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleDone();
                }
              }}
            />
            <TertiaryButton
              className="h-6 py-0 px-1.5 mr-[3px] rounded font-bold"
              disabled={value === ""}
              onClick={() => handleDone()}
            >
              {matcher.value === "" ? "Add" : "Done"}
            </TertiaryButton>
            {deleteButton}
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <div
            className={clsx(
              "flex items-center h-8 border rounded",
              "text-xs pl-2 pr-1 cursor-pointer",
              {
                "border-cosmic-1 bg-cosmic-5 hover:border-cosmic":
                  !matcher.error && !isAboutToDelete,
                "border-mars bg-mars-2": matcher.error || isAboutToDelete,
              },
            )}
            onClick={() => setIsEditing(true)}
          >
            <div className="text-cosmic font-bold text-[11px] leading-[13px]">
              <div className="font-normal text-[9px] leading-[9px]">
                {MATCH_WITH_LABEL[matcher.matchWith]}
              </div>
              {OPERATOR_LABEL[matcher.operator]}
            </div>
            <GenericTooltip
              noInfoCircle
              content={matcher.value}
              className="block ml-2"
            >
              <div className="truncate max-w-[100px]">{matcher.value}</div>
            </GenericTooltip>
            {deleteButton}
          </div>
          {matcher.error && (
            <div className="text-xxs text-mars">*{matcher.error}</div>
          )}
        </div>
      )}
    </>
  );
};
